import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Exam, LiveEvent, QuestionModel, QuizModel } from 'src/app/api/models';
import {
  BooksService,
  CoursesService,
  ExamsService,
  LiveEventsService,
  QuizzesService,
} from 'src/app/api/services';
import { PanelService } from '../panel.service';
import { Panel, PanelEntityType } from '../panel.types';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { ImportEntity, WizardImports } from '../../list/list.types';
import { UserJourneyService } from '../../pages/onboarding-steps/user-journey.service';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmConvertFromQuizComponent,
  ShowInfoItem,
} from '../../common/dialogs/confirm-convert-from-quiz/confirm-convert-from-quiz.component';
import { QuizEditConfigService } from '../../edit/configs/quiz.service';
import {
  INFO_DIALOG_WIDTH,
  settingAnswers,
  settingData,
  validateQuestions,
  validateSchema,
} from 'src/app/utility/app.utils';
import { XLSXReader } from '../../common/import-helpers/xlsxReader';
import * as _ from 'lodash';
import { SourceType } from 'src/app/utility/amplitude-tracking/tracking.types';
import { schemas } from 'src/app/payload-validators';
import { QuestionType } from 'src/app/app.types';

/* TODO: 
  It used in books module which has old import options design.
  so this imports will be removed in future.
*/
export const getQuizImports = (
  translator: TranslateService,
  panelService: PanelService,
  panel: Panel<QuizModel>,
  courseContext = false
): ImportEntity<QuizModel>[] =>
  [
    courseContext
      ? {
          title: translator.instant('content.quiz.addTitle.importExistingQuiz'),
          description: translator.instant(
            'content.quiz.description.importExistingQuiz'
          ),
          action: {
            icon: 'move_to_inbox',
            click: () => panelService.importItems(panel),
            text: translator.instant('content.quiz.addMenu.importExistingQuiz'),
          },
        }
      : null,
    {
      title: translator.instant('content.quiz.addTitle.importQuizFromJSON'),
      description: translator.instant(
        'content.quiz.description.importQuizFromJSON'
      ),
      action: {
        icon: 'code',
        click: () => panelService.importItemAsJSON(panel),
        text: translator.instant('content.quiz.addMenu.importFromJSON'),
      },
    },
    courseContext
      ? {
          title: translator.instant('content.quiz.importfromKahootTitle'),
          description: translator.instant('content.quiz.importFromKahootDesc'),
          action: {
            text: translator.instant('content.quiz.importFromKahoot'),
            icon: 'description',
            click: () => {},
          },
        }
      : null,
    {
      title: translator.instant(
        'content.quiz.addTitle.importQuizFromMoodleXML'
      ),
      description: translator.instant(
        'content.quiz.description.importQuizFromMoodleXML'
      ),
      action: {
        icon: 'code',
        click: () => panelService.importItemAsMoodleXML(panel),
        text: translator.instant('content.quiz.addMenu.importFromMoodleXML'),
      },
    },
  ].filter((n) => n);

export const getWizardQuizImports = (
  translator: TranslateService,
  panelService: PanelService,
  panel: Panel<QuizModel>,
  courseContext = false,
  importFromKahoot?: () => void
): WizardImports => {
  return {
    createWithAI: {
      text: translator.instant('content.wizardActions.title.createWithAI'),
      icon: 'auto_awesome',
      description: translator.instant(
        'content.wizardActions.description.createQuizWithAI'
      ),
      tag: translator.instant('content.wizardActions.tag.createWithAI'),
      click: () => {
        panelService.createWithAI(panel);
      },
    },
    createButton: {
      text: translator.instant('content.wizardActions.title.createManually'),
      icon: 'edit',
      description: translator.instant(
        'content.wizardActions.description.createManually'
      ),
      click: () => panelService.editItem(panel),
    },
    importButtons: [
      {
        text: translator.instant('content.wizardActions.title.useExistingQuiz'),
        description: translator.instant(
          'content.wizardActions.description.useExistingQuiz'
        ),
        icon: 'file_copy',
        hidden: !courseContext,
        click: () => panelService.importItems(panel),
      },
      {
        id: 'import-json',
        text: translator.instant('content.wizardActions.title.importFromJSON'),
        description: translator.instant(
          'content.wizardActions.description.importFromJSON'
        ),
        icon: 'save_alt',
        click: () => panelService.importItemAsJSON(panel),
      },
      {
        id: 'import-csv',
        text: translator.instant('content.wizardActions.title.importFromCSV'),
        description: translator.instant(
          'content.wizardActions.description.importFromCSV'
        ),
        icon: 'save_alt',
        click: () => panelService.createWithCSV(panel),
      },
      {
        id: 'import-kahoot',
        text: translator.instant(
          'content.wizardActions.title.importFromKahoot'
        ),
        description: translator.instant(
          'content.wizardActions.description.importFromKahoot'
        ),
        hidden: !courseContext,
        icon: 'save_alt',
        click: () => importFromKahoot(),
      },
      {
        id: 'import-xml',
        text: translator.instant(
          'content.wizardActions.title.importQuizFromXML'
        ),
        description: translator.instant(
          'content.wizardActions.description.importQuizFromXML'
        ),
        icon: 'save_alt',
        click: () => panelService.importItemAsMoodleXML(panel),
      },
    ].filter((n) => n),
  };
};

@Injectable()
export class QuizPanel extends Panel<QuizModel> {
  isBook = false;

  constructor(
    private courses: CoursesService,
    private books: BooksService,
    private panelService: PanelService,
    private quizzes: QuizzesService,
    private exams: ExamsService,
    private router: Router,
    private translator: TranslateService,
    private auth: AuthService,
    private matSnackbar: MatSnackBar,
    private events: LiveEventsService,
    private userJourney: UserJourneyService,
    private matDialog: MatDialog
  ) {
    super();
    this.initializePanel();
  }

  initializePanel() {
    const imports = getQuizImports(
      this.translator,
      this.panelService,
      this,
      true
    );

    // -2 is added because last 2nd option is Kahoot import
    imports[imports.length - 2].action.click = () => this.importFromKahoot();
    imports[imports.length - 2].extra = {
      name: this.translator.instant('content.quiz.extra'),
      type: 'link',
      icon: 'keyboard_arrow_right',
      click: () => {
        window.open(
          'https://quizacademy.de/tipps/kahoot-inhalte-importieren/',
          '_blank'
        );
      },
    };
    this.isBook =
      location?.pathname?.split('/')?.[3] === 'book' ||
      this.router.url.indexOf('/book') !== -1;
    super.init({
      addMenuItems: [
        {
          icon: 'add',
          onClick: () => this.panelService.editItem(this),
          text: this.translator.instant('content.quiz.addMenu.createNewQuiz'),
        },
      ],
      imports: imports,
      wizardImports: getWizardQuizImports(
        this.translator,
        this.panelService,
        this,
        true,
        this.importFromKahoot.bind(this)
      ),
      childTypes: [PanelEntityType.Question],
      changeItemPosition: (quizId, courseId, position) => {
        const query = {
          id: courseId,
          quiz_id: quizId,
          body: { position },
        };
        return this.isBook
          ? this.books.repositionQuizInBook(query)
          : this.courses.repositionQuizInCourse(query);
      },
      changeItemStatus: (quizId, courseId, active) => {
        const query = {
          id: courseId,
          quiz_id: quizId,
          body: { active },
        };
        return this.isBook
          ? this.books.repositionQuizInBook(query)
          : this.courses.repositionQuizInCourse(query);
      },
      createItemsFn: (items, parentId, source) => {
        const sentryInfo = {
          title: `Error while creating quiz ${
            !parentId
              ? ''
              : 'in ' + (this.isBook ? 'book - ' : 'course - ') + parentId
          }`,
          file: 'quiz.service.ts - create handler',
          api: !parentId
            ? 'createQuiz'
            : this.isBook
            ? 'copyQuizInBook'
            : 'copyQuizToCourse',
        };
        const { isInvalidSchema, errorMessage } = validateSchema(
          schemas.QuizModel.array(),
          items,
          this.matSnackbar,
          sentryInfo
        );
        if (isInvalidSchema) {
          return of({ error: errorMessage });
        }
        if (!parentId) {
          return forkJoin(
            items.map((item) =>
              this.quizzes.createQuiz({
                body: item,
                source,
              })
            )
          );
        } else {
          const query = {
            id: parentId,
            body: items,
            copyQuestions: true,
            source,
          };
          return this.isBook
            ? this.books.copyQuizInBook(query)
            : this.courses.copyQuizToCourse(query);
        }
      },
      deleteItem: (quizId) => this.quizzes.deleteQuiz({ id: quizId }),
      editConfigService: QuizEditConfigService,
      editConfigServiceParams: (_, courseId) => ({ courseId }),
      editDialogInput: (quiz, courseId) => ({ quiz, courseId }),
      fetchItem: (id) => this.quizzes.getQuizById({ id }),
      fetchItems: (courseId) => {
        if (courseId && !_.isNaN(courseId)) {
          return this.isBook
            ? this.books.getQuizForBook({ id: courseId })
            : this.courses.getQuizInCourse({ id: courseId });
        } else {
          return of(null);
        }
      },
      headerIcon: 'library_books',
      isItemActive: (quiz) => quiz.active,
      activateItemAction: {
        icon: 'visibility',
        label: this.translator.instant('content.quiz.actionMenu.activate'),
        onClick: (quiz) =>
          this.panelService.changeItemStatus(this, quiz.id, !quiz.active),
      },
      itemOnHoverActions: [
        {
          icon: 'edit',
          hidden: () => true,
          label: this.translator.instant('common.tooltip.edit'),
          disabled: (quiz) => quiz['isImported'],
          tooltip: (quiz) =>
            quiz['isImported']
              ? this.translator.instant('content.quiz.notYourQuiz')
              : null,
          onClick: (quiz) => this.panelService.editItem(this, quiz),
        },
        {
          icon: 'timeline',
          label: this.translator.instant(
            'content.quiz.actionMenu.openStatistics'
          ),
          onClick: (quiz) => {
            this.panelService.addParam(PanelEntityType.Quiz, quiz.id, {
              showStatistics: true,
            });
          },
        },
      ],
      itemActions: [
        {
          icon: 'play_arrow',
          label: this.translator.instant(
            'content.quiz.actionMenu.convertToLiveEvent'
          ),
          onClick: (quiz) => {
            const dialogRef = this.matDialog.open<
              ConfirmConvertFromQuizComponent,
              ShowInfoItem
            >(ConfirmConvertFromQuizComponent, {
              data: {
                modelTitle: this.translator.instant(
                  'course.confirmAction.convertQuizToEvent'
                ),
                modelInfo: this.translator.instant(
                  'course.confirmAction.convertQuizToEventBottomText'
                ),
                modelType: 'Course',
                confirmButtonText: this.translator.instant(
                  'course.confirmAction.convertToEvent'
                ),
              },
              width: INFO_DIALOG_WIDTH,
              height: 'auto',
              autoFocus: false,
              panelClass: 'confirm-convert-quiz-modal',
            });
            dialogRef.afterClosed().subscribe((resp) => {
              if (resp) {
                const courseId = this.panelService.getParentItemId(
                  PanelEntityType.Quiz
                );
                this.convertQuizToEvent(
                  quiz,
                  this.quizzes,
                  this.events,
                  resp.convert_from_quiz ? courseId : null,
                  this.isBook
                );
              }
            });
          },
        },
        ...(this.isBook
          ? []
          : [
              {
                icon: 'transform',
                label: this.translator.instant(
                  'content.quiz.actionMenu.convertToExam'
                ),
                onClick: (quiz) => {
                  const dialogRef = this.matDialog.open<
                    ConfirmConvertFromQuizComponent,
                    ShowInfoItem
                  >(ConfirmConvertFromQuizComponent, {
                    data: {
                      modelTitle: this.translator.instant(
                        'course.confirmAction.convertQuizToExam'
                      ),
                      modelInfo: this.translator.instant(
                        'course.confirmAction.convertQuizToExamBottomText'
                      ),
                      modelType: 'Course',
                      confirmButtonText: this.translator.instant(
                        'course.confirmAction.convertToExam'
                      ),
                    },
                    width: INFO_DIALOG_WIDTH,
                    height: 'auto',
                    autoFocus: false,
                    panelClass: 'confirm-convert-quiz-modal',
                  });
                  dialogRef.afterClosed().subscribe((resp) => {
                    if (resp) {
                      this.convertToExam(
                        quiz,
                        this.courses,
                        this.panelService,
                        this.quizzes,
                        this.exams,
                        this.matSnackbar,
                        this.translator,
                        this.router,
                        resp.convert_from_quiz
                      );
                    }
                  });
                },
              },
            ]),
        {
          icon: 'save_alt',
          label: this.translator.instant(
            'content.quiz.actionMenu.downloadJSON'
          ),
          onClick: (item) => this.panelService.exportItemAsJSON(this, item.id),
        },
        {
          icon: 'description',
          label: this.translator.instant('content.quiz.actionMenu.downloadCSV'),
          onClick: (item) => this.panelService.exportItemAsCSV(this, item.id),
        },
        {
          icon: 'code',
          label: this.translator.instant('content.quiz.actionMenu.embed'),
          onClick: (quiz) => this.panelService.embedItem(this, quiz),
        },
        {
          icon: 'link_off',
          label: this.isBook
            ? this.translator.instant('content.quiz.actionMenu.removeFromBook')
            : this.translator.instant(
                'content.quiz.actionMenu.removeFromCourse'
              ),
          onClick: (quiz) => this.panelService.unlinkItems(this, quiz.id),
        },
        {
          icon: 'delete',
          label: this.translator.instant('content.quiz.actionMenu.delete'),
          onClick: (quiz) => this.panelService.deleteItem(this, quiz.id),
          disabled: (quiz) =>
            quiz.created_by !== this.auth.currentUserInfo$.value.sub,
          tooltip: (quiz) => {
            if (quiz.created_by !== this.auth.currentUserInfo$.value.sub) {
              return this.translator.instant('content.quiz.notYourQuiz');
            } else {
              return null;
            }
          },
        },
      ],
      itemTextProperty: 'name',
      itemHeaderText: 'quiz.table.name',
      itemHeaderActionText: 'course.form.active',
      parentKey: 'quizzes',
      parentType: PanelEntityType.Course,
      selectedItemActions: [
        {
          icon: 'link_off',
          label: this.isBook
            ? this.translator.instant(
                'content.quiz.selectedItem.removeFromBook'
              )
            : this.translator.instant(
                'content.quiz.selectedItem.removeFromCourse'
              ),
          onClick: (selectedItemIds) =>
            this.panelService.unlinkItems(this, selectedItemIds),
        },
      ],
      translations: {
        addItem: this.translator.instant('content.quiz.tooltip'),
        changeItemStatusDisabled: this.translator.instant(
          'content.quiz.changeItem.statusDisabled'
        ),
        changeItemStatusEnabled: this.translator.instant(
          'content.quiz.changeItem.statusEnabled'
        ),
        deleteItemError: this.translator.instant(
          'content.quiz.deleteItem.error'
        ),
        deleteItemSuccess: this.translator.instant(
          'content.quiz.deleteItem.success'
        ),
        deleteItemText: this.translator.instant('content.quiz.deleteItem.text'),
        deleteItemTitle: this.translator.instant(
          'content.quiz.deleteItem.title'
        ),
        header: 'Quiz',
        unlinkItemConfirmText: this.isBook
          ? this.translator.instant('content.quiz.unlinkItem.textBook')
          : this.translator.instant('content.quiz.unlinkItem.text'),
        unlinkItemConfirmTitle: this.isBook
          ? this.translator.instant('content.quiz.unlinkItem.titleBook')
          : this.translator.instant('content.quiz.unlinkItem.title'),
        unlinkItemError: this.isBook
          ? this.translator.instant('content.quiz.unlinkItem.errorBook')
          : this.translator.instant('content.quiz.unlinkItem.error'),
        unlinkItemSuccess: this.isBook
          ? this.translator.instant('content.quiz.unlinkItem.successBook')
          : this.translator.instant('content.quiz.unlinkItem.success'),
      },
      type: PanelEntityType.Quiz,
      unlinkItems: (quizIds, courseId) => {
        const query = {
          id: courseId,
          quiz_ids: quizIds.join(','),
        };
        return this.isBook
          ? this.books.removeQuizFromBook(query)
          : this.courses.removeQuizFromCourse(query);
      },
    });
  }

  async importFromKahoot() {
    const excelFile = await this.panelService.selectExcelFileAsBinary();
    const file = XLSXReader.excelToArray(excelFile);
    const name = file[0][0];
    if (!name || !name.trim()) {
      this.matSnackbar.open(
        this.translator.instant(
          'content.quiz.importFromExcel.provideNameForQuiz'
        )
      );
      return;
    }
    let quiz;
    const snackbar = this.matSnackbar.open(
      this.translator.instant('content.quiz.importFromExcel.creatingQuiz')
    );
    const questions = this.panelService.importQuestionsFromKahoot(excelFile);
    let questionArr = settingData(questions);
    settingAnswers(questionArr);
    questionArr.forEach((question) => {
      if (question['type'] === 'True/False') {
        question['type'] = 1;
        if (question['answers'].length && question['answers'][0]['is_right']) {
          question['is_right'] = question['answers'][0]['is_right'];
        }
        delete question['answers'];
      } else if (question['type'] === 'Multiple Choice') {
        question['type'] = 0;
      } else if (question['type'] === 'Single Choice') {
        question['type'] = 2;
      }
    });
    questionArr = questionArr.filter(
      (q) => q.type === QuestionType.TRUE_FALSE || q.answers?.length
    );
    if (name) {
      const parentId =
        this.panelService.getParentItemId(PanelEntityType.Quiz) ?? null;
      const query = {
        id: parentId,
        body: [{ name: name } as QuizModel],
        copyQuestions: true,
      };

      quiz = await this.courses.copyQuizToCourse(query).toPromise();

      this.matSnackbar.open(
        this.translator.instant(
          'content.quiz.importFromExcel.addQuestionsToQuiz'
        )
      );
      const hasFreeTextQuestion = questionArr.some(
        (q) => q.type === QuestionType.FREETEXT
      );
      if (hasFreeTextQuestion) {
        this.matSnackbar.open(
          this.translator.instant('quiz.message.freeTextNotAllowed')
        );
        return;
      }
      const sentryInfo = {
        title: `Error while importing questions in quiz from kahoot`,
        file: 'quiz-panel.ts - importFromKahoot',
        api: 'copyQuestionToQuiz',
        preventLogs: true,
      };
      const { isValid } = validateQuestions(
        sentryInfo,
        questionArr,
        this.translator,
        this.matSnackbar
      );
      if (!isValid) {
        snackbar.dismiss();
        return;
      }
      await this.quizzes
        .copyQuestionToQuiz({
          id: quiz[0].id,
          body: questionArr,
        })
        .toPromise();
      snackbar.dismiss();
      this.matSnackbar.open(
        this.translator.instant(
          'content.quiz.importFromExcel.quizCreatedSuccessfully'
        )
      );
      this.matDialog.closeAll();
      // this.router.navigate(['cms', 'content', quiz.id, 'questions']);
    }
  }

  async convertQuizToEvent(
    quiz: QuizModel,
    quizzesService: QuizzesService,
    eventsService: LiveEventsService,
    courseId?: number,
    isBook?: boolean
  ) {
    const questions = await quizzesService
      .getQuestionsForQuiz({
        id: quiz.id,
      })
      .toPromise();
    const event = await eventsService
      .createLiveEvent({
        body: {
          name: quiz.name,
        } as LiveEvent,
        source: SourceType.QUIZ,
      })
      .toPromise();
    if (courseId) {
      const query = {
        id: courseId,
        body: event,
      };
      if (isBook) {
        await this.books.addEventToBook(query).toPromise();
      } else {
        await this.courses.addEventToCourse(query).toPromise();
      }
    }
    await firstValueFrom(
      eventsService.linkQuestionToEvent({
        id: event.id,
        body: questions.map((q) => ({ id: q.id } as QuestionModel)),
      })
    );
    this.matSnackbar.open(
      this.translator.instant('content.quiz.convertedEvent')
    );
    if (courseId) {
      const panel = this.panelService.getPanel(PanelEntityType.Event);
      this.panelService.fetchItems(panel);
      panel.open$.next();
      this.panelService.addParam(PanelEntityType.Event, event.id);
    } else if (event?.id) {
      this.router.navigate(['/cms/live-events', event.id]);
    }
  }

  async convertToExam(
    quiz: QuizModel,
    courses: CoursesService,
    panelService: PanelService,
    quizes: QuizzesService,
    exams: ExamsService,
    matSnackBar,
    translator,
    router: Router,
    course: boolean
  ) {
    matSnackBar.open(translator.instant('content.quiz.convertingExam'));

    const courseId = panelService.getParentItemId(PanelEntityType.Quiz);

    const exam = {
      name: quiz.name,
      scoring_schema: 'DEFAULT',
    };

    try {
      const createdExam = course
        ? await firstValueFrom(
            courses.createExamInCourse({
              id: courseId,
              body: exam as Exam,
            })
          )
        : await firstValueFrom(
            exams.createExam({
              body: exam as Exam,
              source: SourceType.QUIZ,
            })
          );
      this.userJourney.set('createdExam');
      await this.linkQuestions(
        quizes,
        quiz.id,
        createdExam.id,
        exams,
        matSnackBar,
        translator,
        panelService,
        router,
        course
      );
    } catch (error) {
      matSnackBar.open(translator.instant('content.quiz.convertExamError'));
    }
  }

  async linkQuestions(
    quizes: QuizzesService,
    quizId: number,
    examId: number,
    exams: ExamsService,
    matSnackBar,
    translator,
    panelService,
    router: Router,
    course: boolean
  ) {
    matSnackBar.open(translator.instant('content.quiz.linkingQuestion'));
    try {
      const questions = await firstValueFrom(
        quizes.getQuestionsForQuiz({ embed_answers: false, id: quizId })
      );

      const questionIds = questions.map((q) => ({ id: q.id } as QuestionModel));

      await firstValueFrom(
        exams.linkQuestionToExam({
          id: examId,
          body: questionIds,
        })
      );
      matSnackBar.open(translator.instant('content.quiz.converrtedExam'));
      if (course) {
        const panel = panelService.getPanel(PanelEntityType.Exam);
        panelService.fetchItems(panel);
        panel.open$.next();
        panelService.addParam(PanelEntityType.Exam, examId);
      } else {
        router.navigate(['/cms/exams', examId]);
      }
    } catch (error) {
      matSnackBar.open(
        translator.instant('content.quiz.linkingQuestionsError')
      );
    }
  }
}
